import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { jsPDF } from 'jspdf';
import { formatDate } from './config';

const formatCurrency = (value) => `Rs ${Number(value || 0).toFixed(2)}`;
const formatNumber = (value, digits = 2) => Number(value || 0).toFixed(digits);

const UNICODE_FONT_NAME = 'Devanagari';
const UNICODE_FONT_FILE_NAME = 'Devanagari.ttf';
const UNICODE_FONT_URLS = ['/fonts/NotoSansDevanagari-Regular.ttf', '/fonts/Mangal.ttf'];

const sanitizeFileName = (value) =>
    String(value || '')
        .replace(/[^a-z0-9-_]+/gi, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .toLowerCase();

let unicodeFontBase64Cache = null;
let unicodeFontLoadPromise = null;

const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    const chunks = [];
    for (let i = 0; i < bytes.length; i += chunkSize) {
        chunks.push(String.fromCharCode(...bytes.subarray(i, i + chunkSize)));
    }
    return btoa(chunks.join(''));
};

const isProbablyFontFile = (buffer) => {
    if (!buffer || buffer.byteLength < 4) return false;
    const bytes = new Uint8Array(buffer.slice(0, 4));
    const asText = String.fromCharCode(...bytes);
    if (asText === 'OTTO') return true;
    if (asText === 'ttcf') return true;
    return bytes[0] === 0x00 && bytes[1] === 0x01 && bytes[2] === 0x00 && bytes[3] === 0x00;
};

const hasDevanagariText = (value) => /[\u0900-\u097F]/.test(String(value || ''));

const loadUnicodeFontBase64 = async () => {
    if (unicodeFontBase64Cache) return unicodeFontBase64Cache;
    if (unicodeFontLoadPromise) return unicodeFontLoadPromise;
    unicodeFontLoadPromise = (async () => {
        for (const url of UNICODE_FONT_URLS) {
            try {
                const res = await fetch(url, { cache: 'force-cache' });
                if (!res.ok) continue;
                const buffer = await res.arrayBuffer();
                if (!isProbablyFontFile(buffer)) continue;
                const base64 = arrayBufferToBase64(buffer);
                unicodeFontBase64Cache = base64;
                return base64;
            } catch (e) {
                continue;
            }
        }
        return null;
    })();
    return unicodeFontLoadPromise;
};

const ensureUnicodeFont = async (doc) => {
    try {
        const list = typeof doc.getFontList === 'function' ? doc.getFontList() : null;
        if (list && list[UNICODE_FONT_NAME]) {
            doc.setFont(UNICODE_FONT_NAME, 'normal');
            return true;
        }
        const base64 = await loadUnicodeFontBase64();
        if (!base64) return false;
        try {
            doc.addFileToVFS(UNICODE_FONT_FILE_NAME, base64);
            doc.addFont(UNICODE_FONT_FILE_NAME, UNICODE_FONT_NAME, 'normal');
            doc.setFont(UNICODE_FONT_NAME, 'normal');
        } catch (e) {
            unicodeFontBase64Cache = null;
            unicodeFontLoadPromise = null;
            return false;
        }
        return true;
    } catch (e) {
        return false;
    }
};

const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const result = String(reader.result || '');
            const [, base64 = ''] = result.split(',');
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });

const downloadBlob = (blob, fileName) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
};

const drawRow = (doc, cells, columnWidths, startX, startY, rowHeight) => {
    let cursorX = startX;
    cells.forEach((cell, index) => {
        doc.rect(cursorX, startY, columnWidths[index], rowHeight);
        const text = Array.isArray(cell) ? cell : [String(cell ?? '')];
        doc.text(text, cursorX + 4, startY + 14);
        cursorX += columnWidths[index];
    });
};

const fitText = (doc, value, width) => doc.splitTextToSize(String(value ?? '-'), Math.max(12, width - 4));

const formatPaymentSplit = (cash, upi, bank) => {
    const c = Number(cash) || 0;
    const u = Number(upi) || 0;
    const b = Number(bank) || 0;
    const parts = [];
    if (c > 0) parts.push(`Cash ${formatCurrency(c)}`);
    if (u > 0) parts.push(`UPI ${formatCurrency(u)}`);
    if (b > 0) parts.push(`Bank ${formatCurrency(b)}`);
    return parts.length > 0 ? parts.join(' | ') : '-';
};

const getReportFileName = ({ customerName, fromDate, toDate }) => {
    const customerSlug = sanitizeFileName(customerName || 'customer');
    return `sale-report-${customerSlug || 'customer'}-${fromDate || 'from'}-to-${toDate || 'to'}.pdf`;
};

const buildSaleInvoicePdf = async ({ invoice, customer, vehicle, previousBalance }) => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const unicodeReady = await ensureUnicodeFont(doc);
    const headerText = `${customer?.name || invoice?.customer_name || ''}${vehicle?.vehicle_no || invoice?.vehicle_no || ''}`;
    if (!unicodeReady && hasDevanagariText(headerText)) {
        throw new Error(
            'Unicode font missing for Marathi. Upload NotoSansDevanagari-Regular.ttf to /public/fonts and redeploy.'
        );
    }
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 36;
    const columnWidths = [190, 45, 70, 70, 55, 93];
    const totalWidth = columnWidths.reduce((sum, width) => sum + width, 0);
    const startX = margin;
    let cursorY = margin;

    const invoiceId = invoice?.id || invoice?.invoice_id || 'New';
    const totalAmount = Number(invoice?.total_amount || 0);
    const paidAmount = Number(invoice?.paid_amount || 0);
    const prevAmount = Number(previousBalance || 0);
    const remainingAmount = prevAmount + totalAmount - paidAmount;
    const items = Array.isArray(invoice?.items) ? invoice.items : [];
    const paymentLines = Array.isArray(invoice?.payments) ? invoice.payments : [];

    doc.setFontSize(18);
    doc.text('Sale Invoice', margin, cursorY);
    cursorY += 24;

    doc.setFontSize(10);
    doc.text(`Invoice No: ${invoiceId}`, margin, cursorY);
    doc.text(`Date: ${formatDate(invoice?.date)}`, pageWidth - margin - 120, cursorY);
    cursorY += 18;
    doc.text(`Customer: ${customer?.name || invoice?.customer_name || '-'}`, margin, cursorY);
    cursorY += 14;
    doc.text(`Contact: ${customer?.contact || invoice?.customer_contact || '-'}`, margin, cursorY);
    cursorY += 14;
    doc.text(`Vehicle: ${vehicle?.vehicle_no || invoice?.vehicle_no || '-'}`, margin, cursorY);
    cursorY += 20;

    doc.setFillColor(240, 240, 240);
    doc.rect(startX, cursorY, totalWidth, 22, 'F');
    drawRow(doc, ['Product', 'Qty', 'Weight', 'Rate', 'Disc', 'Amount'], columnWidths, startX, cursorY, 22);
    cursorY += 22;

    items.forEach((item) => {
        const productLines = doc.splitTextToSize(String(item?.product_name || '-'), columnWidths[0] - 8);
        const rowHeight = Math.max(20, productLines.length * 12 + 8);

        if (cursorY + rowHeight > pageHeight - 170) {
            doc.addPage();
            cursorY = margin;
            doc.setFillColor(240, 240, 240);
            doc.rect(startX, cursorY, totalWidth, 22, 'F');
            drawRow(doc, ['Product', 'Qty', 'Weight', 'Rate', 'Disc', 'Amount'], columnWidths, startX, cursorY, 22);
            cursorY += 22;
        }

        drawRow(
            doc,
            [
                productLines,
                String(Number(item?.quantity || 0)),
                Number(item?.weight || 0).toFixed(2),
                Number(item?.price || 0).toFixed(2),
                Number(item?.discount_rate || 0).toFixed(2),
                Number(item?.total || 0).toFixed(2)
            ],
            columnWidths,
            startX,
            cursorY,
            rowHeight
        );
        cursorY += rowHeight;
    });

    cursorY += 18;
    const paymentSummary = paymentLines.length > 0
        ? paymentLines.map((line) => `${line.payment_mode}: ${formatCurrency(line.amount)}`).join(' | ')
        : '-';

    [
        `Net Amount: ${formatCurrency(totalAmount)}`,
        `Previous Amount: ${formatCurrency(prevAmount)}`,
        `Paid Amount: ${formatCurrency(paidAmount)}`,
        `Payment Split: ${paymentSummary}`,
        `Balance Amount: ${formatCurrency(remainingAmount)}`
    ].forEach((line) => {
        if (cursorY > pageHeight - 80) {
            doc.addPage();
            cursorY = margin;
        }
        doc.text(line, margin, cursorY);
        cursorY += 16;
    });

    return doc;
};

const tryNativeShare = async ({ blob, fileName, title, text }) => {
    if (!Capacitor.isNativePlatform()) return false;
    const base64 = await blobToBase64(blob);
    const path = `shared-invoices/${fileName}`;
    const writeResult = await Filesystem.writeFile({
        path,
        data: base64,
        directory: Directory.Cache,
        recursive: true
    });

    await Share.share({
        title,
        text,
        url: writeResult.uri,
        dialogTitle: 'Share Sale Invoice PDF'
    });
    return true;
};

const tryBrowserShare = async ({ blob, fileName, title, text }) => {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
    const file = new File([blob], fileName, { type: 'application/pdf' });
    if (typeof navigator.canShare !== 'function' || !navigator.canShare({ files: [file] })) {
        return false;
    }
    await navigator.share({
        title,
        text,
        files: [file]
    });
    return true;
};

export const shareSaleInvoicePdf = async ({ invoice, customer, vehicle, previousBalance }) => {
    const invoiceId = invoice?.id || invoice?.invoice_id || 'invoice';
    const customerSlug = sanitizeFileName(customer?.name || invoice?.customer_name || 'customer');
    const fileName = `sale-invoice-${invoiceId}-${customerSlug || 'customer'}.pdf`;
    const title = `Sale Invoice #${invoiceId}`;
    const text = `Sharing Sale Invoice #${invoiceId}`;
    const pdf = await buildSaleInvoicePdf({ invoice, customer, vehicle, previousBalance });
    const blob = pdf.output('blob');

    try {
        const shared = await tryNativeShare({ blob, fileName, title, text });
        if (shared) return { shared: true, mode: 'native' };
    } catch (error) {
        console.error('Native PDF share failed', error);
    }

    try {
        const shared = await tryBrowserShare({ blob, fileName, title, text });
        if (shared) return { shared: true, mode: 'browser' };
    } catch (error) {
        console.error('Browser PDF share failed', error);
    }

    downloadBlob(blob, fileName);
    return { shared: false, mode: 'download' };
};

const buildSalesReportPdf = async ({ rows, title, customerName, fromDate, toDate }) => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
    const unicodeReady = await ensureUnicodeFont(doc);
    if (!unicodeReady && hasDevanagariText(customerName)) {
        throw new Error(
            'Unicode font missing for Marathi. Upload NotoSansDevanagari-Regular.ttf to /public/fonts and redeploy.'
        );
    }
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 18;
    const startX = margin;
    const columnWidths = [64, 40, 52, 52, 72, 52, 92, 52, 92, 60];
    const totalWidth = columnWidths.reduce((sum, width) => sum + width, 0);
    const headers = [
        'Date',
        'Qty',
        'Weight',
        'Rate',
        'Invoice Total',
        'Paid Amount',
        'Paid Split',
        'Receipt',
        'Receipt Split',
        'Net'
    ];
    let cursorY = margin + 8;

    const drawHeader = () => {
        doc.setFontSize(13);
        doc.text(title || 'Sales Report', startX, cursorY);
        cursorY += 14;

        doc.setFontSize(8);
        doc.text(`Customer: ${customerName || '-'}`, startX, cursorY);
        doc.text(`From: ${formatDate(fromDate)}`, pageWidth / 2 - 45, cursorY);
        doc.text(`To: ${formatDate(toDate)}`, pageWidth - margin - 75, cursorY);
        cursorY += 12;

        doc.setFillColor(235, 235, 235);
        doc.rect(startX, cursorY, totalWidth, 18, 'F');
        let headX = startX;
        headers.forEach((header, index) => {
            doc.rect(headX, cursorY, columnWidths[index], 18);
            doc.text(fitText(doc, header, columnWidths[index]), headX + 2, cursorY + 11);
            headX += columnWidths[index];
        });
        cursorY += 18;
    };

    drawHeader();
    doc.setFontSize(6.5);

    rows.forEach((row) => {
        const cells = [
            formatDate(row.date),
            String(Number(row.total_qty || 0)),
            formatNumber(row.total_weight, 2),
            formatNumber(row.rate_per_kg, 0),
            formatCurrency(row.total_amount),
            formatCurrency(row.paid_amount),
            formatPaymentSplit(row.cash_amount, row.upi_amount, row.bank_amount),
            formatCurrency(row.receipt_amount),
            formatPaymentSplit(row.receipt_cash_amount, row.receipt_upi_amount, row.receipt_bank_amount),
            formatCurrency(row.cumulative_balance ?? row.net_balance)
        ];
        const linesByCell = cells.map((cell, index) => fitText(doc, cell, columnWidths[index]));
        const maxLines = linesByCell.reduce((max, lines) => Math.max(max, lines.length), 1);
        const rowHeight = Math.max(16, maxLines * 8 + 4);

        if (cursorY + rowHeight > pageHeight - margin) {
            doc.addPage();
            cursorY = margin + 8;
            drawHeader();
            doc.setFontSize(6.5);
        }

        let cellX = startX;
        linesByCell.forEach((lines, index) => {
            doc.rect(cellX, cursorY, columnWidths[index], rowHeight);
            doc.text(lines, cellX + 2, cursorY + 9);
            cellX += columnWidths[index];
        });
        cursorY += rowHeight;
    });

    if (!rows.length) {
        doc.text('No sales found for this period.', startX, cursorY + 12);
    }

    return doc;
};

export const shareSalesReportPdf = async ({ rows, customerName, fromDate, toDate }) => {
    const fileName = getReportFileName({ customerName, fromDate, toDate });
    const title = 'Sales Report';
    const pdf = await buildSalesReportPdf({
        rows: Array.isArray(rows) ? rows : [],
        title: 'Sales Report',
        customerName,
        fromDate,
        toDate
    });
    const blob = pdf.output('blob');

    try {
        const shared = await tryNativeShare({
            blob,
            fileName,
            title,
            text: `Sales Report ${formatDate(fromDate)} to ${formatDate(toDate)}`
        });
        if (shared) return { shared: true, mode: 'native' };
    } catch (error) {
        console.error('Native Sales Report PDF share failed', error);
    }

    try {
        const shared = await tryBrowserShare({
            blob,
            fileName,
            title,
            text: `Sales Report ${formatDate(fromDate)} to ${formatDate(toDate)}`
        });
        if (shared) return { shared: true, mode: 'browser' };
    } catch (error) {
        console.error('Browser Sales Report PDF share failed', error);
    }

    downloadBlob(blob, fileName);
    return { shared: false, mode: 'download' };
};
